import React from "react";
import { motion } from "framer-motion";
import {
  ShieldAlert,
  Lock,
  Server,
  Bug,
  Cloud,
  UserCheck
} from "lucide-react";
import Navbar from "../../components/Navbar";

const cyberServices = [
  {
    title: "Information Security Management (ISO 27001)",
    desc: "Design and implementation of ISO 27001 compliant Information Security Management Systems (ISMS).",
    icon: ShieldAlert
  },
  {
    title: "Cyber Risk Assessment",
    desc: "Identification, analysis, and mitigation of cyber risks across IT infrastructure and operations.",
    icon: Bug
  },
  {
    title: "Network & Infrastructure Security",
    desc: "Securing servers, networks, firewalls, and endpoints against cyber threats.",
    icon: Server
  },
  {
    title: "Data Protection & Privacy",
    desc: "Implementation of data protection controls, access management, and privacy safeguards.",
    icon: Lock
  },
  {
    title: "Cloud Security",
    desc: "Security assessment and controls for cloud-based systems and digital platforms.",
    icon: Cloud
  },
  {
    title: "User Awareness & Access Control",
    desc: "Employee awareness training and secure role-based access management.",
    icon: UserCheck
  }
];

const ITCyberSecurity = () => {
  return (
    <>
    <Navbar />
    <section className="bg-gray-50 py-14 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto">

        {/* ================= HEADER ================= */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="max-w-3xl mx-auto text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-[var(--bg-primary)] mb-4">
            IT & Cyber Security
          </h2>
          <div className="w-20 h-1 bg-[var(--bg-primary)] mx-auto rounded-full mb-4"></div>
          <p className="text-gray-600 text-base md:text-lg">
            MBA Solutions delivers comprehensive IT and Cyber Security
            consultancy services to protect digital assets, ensure data
            confidentiality, and strengthen organizational resilience against
            cyber threats.
          </p>
        </motion.div>

        {/* ================= CONTENT ================= */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">

          {/* LEFT – Tech Illustration Blocks */}
          <div className="grid grid-cols-2 gap-6">
            {cyberServices.slice(0, 4).map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.85 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, ease: "easeOut", delay: index * 0.1 }}
                  whileHover={{
                    y: -8,
                    scale: 1.05,
                    boxShadow: "0px 20px 40px rgba(59,130,246,0.25)"
                  }}
                  className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 relative group"
                >
                  <div className="w-12 h-12 rounded-xl bg-[var(--bg-primary)] text-white flex items-center justify-center mb-4 shadow-md">
                    <Icon size={24} />
                  </div>

                  <h3 className="text-sm font-bold text-gray-900 mb-1">
                    {item.title}
                  </h3>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    {item.desc}
                  </p>

                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500/10 to-indigo-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </motion.div>
              );
            })}
          </div>

          {/* RIGHT – Structured Explanation */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-5">
              End-to-End Cyber Protection
            </h3>

            <p className="text-gray-600 leading-relaxed mb-6">
              Our IT & Cyber Security solutions are designed to help
              organizations proactively identify vulnerabilities, protect
              critical systems, and comply with international information
              security standards.
            </p>

            <ul className="space-y-4">
              {cyberServices.slice(4).map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className="flex items-start gap-4"
                  >
                    <div className="w-10 h-10 rounded-full bg-[var(--bg-primary)] text-white flex items-center justify-center shadow-md">
                      <Icon size={18} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-sm">
                        {item.title}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {item.desc}
                      </p>
                    </div>
                  </motion.li>
                );
              })}
            </ul>

            {/* CTA */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              className="mt-10 px-8 py-3 rounded-full bg-[var(--bg-primary)] text-white font-semibold shadow-lg hover:shadow-2xl transition-all duration-300"
            >
              Secure Your Digital Infrastructure
            </motion.button>
          </motion.div>

        </div>
      </div>
    </section>
    </>
  );
};

export default ITCyberSecurity;
